import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AppBar, Box, Collapse, Drawer, IconButton, List, ListItemButton, ListItemText, Toolbar } from "@mui/material";
import { motion, useAnimation } from "framer-motion";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../AppContext';
import { PowerBiLoginRequest } from "../AuthConfig";
import { MenuItem } from "../models/models";
import { useUnifiedAuthentication } from "../components/CommonComponents";
import { isRunningInTeams, LoginUser } from '../services/AppOwnsDataWebApi'
import { app, authentication } from "@microsoft/teams-js";

interface MenuItemProps {
    name: string;
    link?: string;
    level: number;
    children?: MenuItemProps[];
}

interface MenuProps {
    items: MenuItemProps[];
}

const HeaderMobile = ({ items }: MenuProps) => {
    const isAuthenticated = useUnifiedAuthentication();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
    const rootItems: MenuItem[] = [];
    const { instance } = useMsal();
    const { embeddingData, setDisplayMainLoader, isSuperUser, setIsTeamsAuthenticated, isTeamsAuthenticated, setTeamsUser } = useContext(AppContext);
    const [drawerWidth, setDrawerWidth] = useState('40%');

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const toggleExpand = (name: string) => {
        setExpanded((prev) => ({ ...prev, [name]: !prev[name] }));
    };
    const line1Animation = useAnimation();
    const line2Animation = useAnimation();
    const line3Animation = useAnimation();
    const menuIconRef = useRef(null);

    const animateLines = async (hovered: boolean) => {
        await line1Animation.start({ width: hovered ? '30px' : '30px', transition: { duration: 0.1 } });
        await line2Animation.start({ width: hovered ? '30px' : '30px', transition: { duration: 0.15 } });
        await line3Animation.start({ width: hovered ? '30px' : '25px', transition: { duration: 0.175 } });
    };

    const logoutUser = () => {
        setMenuOpen(false);
        setDisplayMainLoader(false);
        embeddingData.setRedirectUserToReport(false);
        navigate("/");
        if (isTeamsAuthenticated === true)
            setIsTeamsAuthenticated(false);

        if (isTeamsAuthenticated === false)
            instance.logoutRedirect();
    };

    const loginUser = async () => {
        const inTeams = await isRunningInTeams();
        if (inTeams) {
            try {
                const context = await app.getContext();
                const token = await authentication.getAuthToken({ silent: true });
                await LoginUser(context.user?.userPrincipalName, context.user?.displayName);
                setTeamsUser({
                    userPrincipalName: context.user?.userPrincipalName,
                    displayName: context.user?.userPrincipalName,
                });
                setIsTeamsAuthenticated(true);
                navigate("/");
            }
            catch (error) {
                console.error("Teams SSO failed", error);
            }
        }
        else
            instance.loginRedirect(PowerBiLoginRequest);
    };

    useEffect(() => {
        animateLines(false);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            const windowWidth = window.innerWidth;
            let newWidth = '60%';

            if (windowWidth > 1200) {
                newWidth = '40%';
            } else if (windowWidth > 800) {
                newWidth = '50%';
            }

            setDrawerWidth(newWidth);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const permissionBasedObjects: MenuItem[] = [
        {
            id: -1000,
            name: 'SETTINGS',
            level: 0,
            parentId: null,
            link: null,
            displayOrder: 1000,
            isAssigned: true
        },
        {
            id: -1001,
            name: 'MANAGEMENT',
            level: 1,
            parentId: -1000,
            link: null,
            displayOrder: 1000,
            isAssigned: true
        },
        {
            id: -1002,
            name: 'USER',
            level: 2,
            parentId: -1001,
            link: '/usermanagement',
            displayOrder: 0,
            isAssigned: true
        },
        {
            id: -1003,
            name: 'CONTENT',
            level: 2,
            parentId: -1001,
            link: '/contentmanagement',
            isAssigned: true,
            displayOrder: 3
        },
        {
            id: -1004,
            name: 'PACKAGE',
            level: 2,
            parentId: -1001,
            link: '/content-role-management',
            isAssigned: true,
            displayOrder: 2
        },
        {
            id: -1005,
            name: process.env.REACT_APP_VERSION,
            level: 0,
            parentId: null,
            link: null,
            displayOrder: 1000,
            isAssigned: true
        }
    ];


    if (isAuthenticated && isSuperUser) {
        const menuMap: { [id: number]: MenuItem } = {};

        permissionBasedObjects.forEach(item => {
            menuMap[item.id] = { ...item, children: [] };
        });

        permissionBasedObjects.forEach(item => {
            if (item.parentId != null && menuMap[item.parentId]) {
                menuMap[item.parentId].children!.push(menuMap[item.id]);
            } else {
                rootItems.push(menuMap[item.id]);
            }
        });

        const sortItems = (items: MenuItem[]) => {
            items.sort((a, b) => a.displayOrder - b.displayOrder);
            items.forEach(item => {
                if (item.children && item.children.length > 0) {
                    sortItems(item.children);
                }
            });
        };
    }

    const renderMenuItems = (items: MenuItemProps[]) => {
        return items.map((item: MenuItemProps) => (
            <div
                key={item.name}>
                <ListItemButton
                    onClick={(event: any) => {
                        event.preventDefault();
                        event.stopPropagation();
                        navigate(item.link)
                    }}
                    component="a"
                    href={item.children?.length ? undefined : item.link}
                    sx={{
                        paddingTop: '0px',
                        paddingBottom: '0px',
                        paddingLeft: '40px'
                    }}
                >
                    <ListItemText
                        primary={item.name}
                        sx={{
                            fontWeight: "700 !important",
                            textAlign: "left",
                            color: "white",
                            textTransform: "uppercase",
                            fontFamily: "'Barlow', sans-serif !important",
                        }}
                    />
                    {item.children?.length ? (
                        expanded[item.name] ? (
                            <ExpandLessIcon
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    toggleExpand(item.name);
                                }}
                                sx={{ color: "white" }}
                            />
                        ) : (
                            <ExpandMoreIcon
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    toggleExpand(item.name);
                                }}
                                sx={{ color: "white" }}
                            />
                        )
                    ) : null}

                </ListItemButton>
                {item.children?.length ? (
                    <Collapse
                        in={expanded[item.name]}
                        timeout="auto"
                        unmountOnExit>
                        <List sx={{ pl: 2 }}>{renderMenuItems(item.children)}</List>
                    </Collapse>
                ) : null}
            </div>

        ));
    };

    return (
        <AppBar position="fixed"
            sx={{
                backgroundColor: isAuthenticated ? "#00594d" : 'transparent',
                boxShadow: 'none',
                borderBottom: isAuthenticated ? '2px solid white' : '',
                height: "auto",
                minHeight: "60px",
            }}>
            <Toolbar
                sx={{
                    display: "flex",
                    justifyContent: "space-between"
                }}>
                <Box>
                    <img
                        src="https://travelheartfamily.com/wp-content/uploads/2023/09/THF-LOGO-LOGOTYPE-neg.svg"
                        alt="Travelheart Family"
                        style={{
                            maxHeight: '50px',
                            maxWidth: '375px',
                            width: '100%',
                            height: 'auto',
                            opacity: 1,
                            zIndex: 4,
                        }}
                    />
                </Box>
                <IconButton
                    edge="end"
                    color="inherit"
                    onMouseEnter={() => {
                        animateLines(true);
                        toggleMenu();
                    }}
                    onMouseLeave={() => {
                        animateLines(false);
                    }}
                    ref={menuIconRef}
                >
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <motion.div animate={line1Animation} style={{ height: '3px', backgroundColor: 'white', marginBottom: '4px' }} />
                        <motion.div animate={line2Animation} style={{ height: '3px', backgroundColor: 'white', marginBottom: '4px' }} />
                        <motion.div animate={line3Animation} style={{ height: '3px', marginRight: 'auto', backgroundColor: 'white' }} />
                    </Box>
                </IconButton>
            </Toolbar>
            <Drawer
                anchor="right"
                open={menuOpen}
                onClose={toggleMenu}
                style={{ color: 'white' }}
                PaperProps={{
                    sx: { backgroundColor: "#00594d", width: drawerWidth },
                    onMouseLeave: toggleMenu
                }}
            >
                <motion.div
                    initial={{ x: "100%" }}
                    animate={{ x: menuOpen ? 0 : "100%" }}
                    transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                    <IconButton
                        edge="end"
                        color="inherit"
                        sx={{
                            float: 'right',
                            marginRight: '20px',
                            marginTop: '20px',
                        }}
                    >
                        <Box display="flex" flexDirection="column" alignItems="center">
                            <motion.div animate={line1Animation} style={{ height: '3px', backgroundColor: 'white', marginBottom: '4px' }} />
                            <motion.div animate={line2Animation} style={{ height: '3px', backgroundColor: 'white', marginBottom: '4px' }} />
                            <motion.div animate={line3Animation} style={{ height: '3px', marginRight: 'auto', backgroundColor: 'white' }} />
                        </Box>
                    </IconButton>
                    <List sx={{ marginTop: '100px' }}>
                        {isAuthenticated && items && items.length > 0 ? renderMenuItems([...items]) : <ListItemText primary="" />}
                        {isAuthenticated ? renderMenuItems([...rootItems]) : <ListItemText primary="" />}
                        {isAuthenticated ? (
                            <div
                                key={"Logout"}>
                                <ListItemButton
                                    onClick={() => { logoutUser(); }}
                                    component="a"
                                    sx={{
                                        paddingTop: '0px',
                                        paddingBottom: '0px',
                                        paddingLeft: '40px'
                                    }}
                                >
                                    <ListItemText
                                        primary={"Logout"}
                                        sx={{
                                            fontWeight: "700 !important",
                                            textAlign: "left",
                                            color: "white",
                                            textTransform: "uppercase",
                                            fontFamily: "'Barlow', sans-serif !important",
                                        }}
                                    />
                                </ListItemButton>
                            </div>) : (
                            <div
                                key={"LogIn"}>
                                <ListItemButton
                                    onClick={() => { loginUser(); }}
                                    component="a"
                                    sx={{
                                        paddingTop: '0px',
                                        paddingBottom: '0px',
                                        paddingLeft: '40px'
                                    }}
                                >
                                    <ListItemText
                                        primary={"LogIn"}
                                        sx={{
                                            fontWeight: "700",
                                            textAlign: "left",
                                            color: "white",
                                            textTransform: "uppercase",
                                            fontFamily: "'Barlow', sans-serif !important",
                                        }}
                                    />
                                </ListItemButton>
                            </div>
                        )}
                    </List>
                </motion.div>
            </Drawer>
        </AppBar>
    );
};

export default HeaderMobile;

